/**
 * Provider identity shared by the Worker and the client: the one place that decides which agent a
 * reported provider string names. src/client/brands.ts, the definition, card, and bootstrap modules
 * all read from here, so adding an agent is one edit plus its glyph.
 */

export const PROVIDER_IDS = ["claude", "codex", "grok", "muse", "dots", "other"] as const;

export type ProviderId = (typeof PROVIDER_IDS)[number];

/** The agents LoopGroup knows by name; "other" stands for any agent that reports without one. */
export type NamedProviderId = Exclude<ProviderId, "other">;

// OpenAI styles its personal agent in lower case, and the product follows: "Move to dots".
const displayNames: Record<ProviderId, string> = { claude: "Claude", codex: "Codex", grok: "Grok", muse: "Muse", dots: "dots", other: "Other" };

/**
 * Normalizes a reported provider string ("Claude", "openai", "xAI", "Meta Muse", "OpenAI dots") to a
 * known provider id, or null when nothing was reported. dots is checked before Codex because its
 * reports often say "OpenAI dots"; a bare "OpenAI" stays Codex so earlier runs keep their agent.
 */
export function normalizeProvider(value: string | null | undefined): ProviderId | null {
  if (value == null) return null;
  const normalized = value.trim().toLowerCase();
  if (!normalized) return null;
  if (normalized.includes("claude") || normalized.includes("anthropic")) return "claude";
  if (/\bdots?\b/u.test(normalized)) return "dots";
  if (normalized.includes("codex") || normalized.includes("openai")) return "codex";
  if (normalized.includes("grok") || normalized === "xai" || normalized === "x.ai") return "grok";
  if (/\b(muse|meta)\b/u.test(normalized)) return "muse";
  return "other";
}

export function providerDisplayName(id: ProviderId): string {
  return displayNames[id];
}

export function isProviderId(value: unknown): value is ProviderId {
  return typeof value === "string" && (PROVIDER_IDS as readonly string[]).includes(value);
}
